import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import MobileMenu from "@/components/ui/mobile-menu";
import UserProfileButton from "@/components/layout/UserProfileButton";
import ThemeToggle from "@/components/layout/ThemeToggle";
import { useAuth } from "@/contexts/auth";
import Logo from "@/components/shared/Logo";
import { hasLaunchDatePassed } from "@/utils/date-constants";
import ResourcesMenu from "@/components/ui/resources-menu";
import SolutionsMenu from "@/components/ui/solutions-menu";

const Header = () => {
  const { user } = useAuth();
  const isLaunched = hasLaunchDatePassed();
  const homeUrl = isLaunched ? "/" : "/cayman";
  const homeNavState = isLaunched ? { noRedirect: true } : {};
  const navigate = useNavigate();

  // Function to handle hash navigation
  const handleHashNavigation = (
    e: React.MouseEvent<HTMLAnchorElement>,
    hash: string
  ) => {
    e.preventDefault();
    navigate("/", { state: { noRedirect: true } });

    // Give the page time to load before scrolling
    setTimeout(() => {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Main Header */}
      <div className="bg-background/90 backdrop-blur-sm border-b border-border relative z-10">
        <div className="container-full max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="lg:hidden">
              <MobileMenu />
            </div>
            <Logo />
          </div>

          {/* Desktop Navigation (lg and up) */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link
              to={homeUrl}
              state={homeNavState}
              className="text-foreground hover:text-nation-blue transition-colors"
            >
              Home
            </Link>
            {isLaunched && (
              <>
                <Link
                  to="/about"
                  className="text-foreground hover:text-nation-blue transition-colors"
                >
                  About
                </Link>
                <a
                  href="/#how-it-works"
                  className="text-foreground hover:text-nation-blue transition-colors"
                  onClick={(e) => handleHashNavigation(e, "#how-it-works")}
                >
                  How It Works
                </a>
                <SolutionsMenu />
                <ResourcesMenu />
                <Link
                  to="/partners"
                  className="text-foreground hover:text-nation-blue transition-colors"
                >
                  Partners
                </Link>
                <Link
                  to="/contact"
                  className="text-foreground hover:text-nation-blue transition-colors"
                >
                  Contact
                </Link>
              </>
            )}
          </nav>

          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <UserProfileButton />
            ) : (
              <>
                {/* Desktop: Show both login and CTA buttons */}
                <div className="hidden lg:flex items-center gap-3">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/auth">Login</Link>
                  </Button>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/auth" state={{ defaultTab: "signup" }}>
                      Sign Up
                    </Link>
                  </Button>
                  {isLaunched ? (
                    <Button
                      size="sm"
                      className="bg-nation-teal hover:bg-nation-teal-dark text-white"
                      asChild
                    >
                      <a
                        href="/#register"
                        onClick={(e) => handleHashNavigation(e, "#register")}
                      >
                        Become a Pollster
                      </a>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-nation-teal hover:bg-nation-teal-dark text-white"
                      asChild
                    >
                      <Link to="/cayman">Cayman Islands</Link>
                    </Button>
                  )}
                </div>

                {/* Tablet: Show login and primary CTA */}
                <div className="hidden md:flex lg:hidden items-center gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/auth">Login</Link>
                  </Button>
                  {isLaunched ? (
                    <Button
                      size="sm"
                      className="bg-nation-teal hover:bg-nation-teal-dark text-white"
                      asChild
                    >
                      <a
                        href="/#register"
                        onClick={(e) => handleHashNavigation(e, "#register")}
                      >
                        Register
                      </a>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-nation-teal hover:bg-nation-teal-dark text-white"
                      asChild
                    >
                      <Link to="/cayman">Cayman</Link>
                    </Button>
                  )}
                </div>

                {/* Mobile: Show only primary CTA (auth options in mobile menu) */}
                <div className="md:hidden">
                  {isLaunched ? (
                    <Button
                      size="sm"
                      className="bg-nation-teal hover:bg-nation-teal-dark text-white text-xs px-3"
                      asChild
                    >
                      <a
                        href="/#register"
                        onClick={(e) => handleHashNavigation(e, "#register")}
                      >
                        Register
                      </a>
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      className="bg-nation-teal hover:bg-nation-teal-dark text-white text-xs px-3"
                      asChild
                    >
                      <Link to="/cayman">Cayman</Link>
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Branded Tile - Seamlessly attached to header */}
      <div
        className="h-6 bg-cover bg-center bg-no-repeat opacity-100"
        style={{
          backgroundImage: "url('/img/hero-19-1920x480.png')",
          backgroundPosition: "center 20%",
        }}
      />
    </header>
  );
};

export default Header;
